// @ts-check
/// <reference path="au-cart-types.js" />
/// <reference path="au-cart-utils.js" />
/// <reference path="au-cart-dom-helper.js" />
/// <reference path="au-cart-api-client.js" />

/**
 * @class
 * @implements {AuCartInterface}
 */
class auCart {
  /**
   * @type {string}
   */
  RUSH_FEE_HANDLER = 'rush-fee';

  /**
   * @type {string}
   */
  EXTRA_SIDE_HANDLER = 'extra-side';

  /**
   * @type {boolean}
   */
  isAddRushFee = false;

  /**
   * @type {HTMLElement|null}
   */
  cartForm = null;

  /**
   * @type {Array<ShopifyCartItem>}
   */
  cartItems = [];

  /**
   * @type {RushFeeProduct|null}
   */
  rushFeeProduct = null;

  /**
   * @param {RushFeeProduct|null} rushFeeProduct
   */
  constructor(rushFeeProduct) {
    this.rushFeeProduct = rushFeeProduct;
    auCartApiClient
      .getCart()
      .then((cart) => {
        if (cart?.items) {
          this.cartItems = cart.items;
        }
      })
      .finally(() => {
        auCartDomHelper.setDisabledCheckout(true);
        this.bootstrap();
        this.initCartUI();
      });
  }

  bootstrap() {
    this.deleteAllRushFeeProducts()
      .then(() => this.removeObsoleteExtraSides())
      .then(() => auCartApiClient.deleteWhiteLabelCartAttribute());
    this.cartForm = /** @type {HTMLElement|null} */ (
      document.querySelector("form[action='/cart']")
    );
    this.hideExtraSideAndRushFeeProducts();
  }

  async initCartUI() {
    this.updateUIBasedOnCartState();
    this.bindRushFeeCheckbox();
    this.bindWhiteLabelCheckbox();
    await this.updateCartUI();
    document.addEventListener('au_update_cart', () => this.updateCartUI());
    await this.updateCartUI(true);
    this.runPreselectedRushFee();
  }

  runPreselectedRushFee() {
    const queryParams = new URLSearchParams(window.location.search);
    const hasRush = queryParams.get('has-rush');
    if (hasRush) {
      const rushFeeCheckbox = /** @type {HTMLInputElement|null} */ (
        document.querySelector('#additional-rush-fee')
      );
      if (!rushFeeCheckbox) return;
      rushFeeCheckbox.checked = true;
      rushFeeCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
      this.cartItems.forEach((item) => {
        this.updatePriceWithExtras(item);
      });
    }
  }

  hideExtraSideAndRushFeeProducts() {
    this.cartItems.forEach((item) => {
      if (
        item.handle === this.EXTRA_SIDE_HANDLER ||
        item.handle === this.RUSH_FEE_HANDLER
      ) {
        this.hideLineItem(item);
      }
    });
  }

  /**
   * Replace location search params without reloading page
   * @param {string} newSearch 
   */
  replaceSearch(newSearch) {
    const newUrl = new URL(window.location.href);
    newUrl.search = newSearch;
    history.replaceState({}, '', newUrl.href);
  }

  /**
   * Binds the Rush Fee checkbox event listener.
   */
  bindRushFeeCheckbox() {
    const rushFeeCheckbox = /** @type {HTMLInputElement|null} */ (
      document.querySelector('#additional-rush-fee')
    );
    if (!rushFeeCheckbox) return;
    rushFeeCheckbox.onchange = async (event) => {
      const target = /** @type {HTMLInputElement} */ (event.target);
      const isChecked = target.checked;
      target.disabled = true;
      try {
        const mainItems = this.cartItems.filter(
          (item) =>
            item.properties &&
            item.properties._projectId &&
            item.handle !== this.EXTRA_SIDE_HANDLER &&
            item.handle !== this.RUSH_FEE_HANDLER
        );
        const rushQuantity = mainItems.reduce(
          (acc, item) => acc + item.quantity,
          0
        );
        this.isAddRushFee = isChecked;
        if (isChecked && this.rushFeeProduct) {
          await auCartApiClient.addToCart(
            this.rushFeeProduct.variants[0].id,
            rushQuantity
          );
          this.replaceSearch(`has-rush=true`);
        } else if (!isChecked) {
          await this.deleteAllRushFeeProducts();
          this.replaceSearch(`has-rush=false`);
        }
        this.updateCartUI();
      } catch (error) {
        console.error('Error while updating rush fee option', error);
      } finally {
        target.disabled = false;
      }
    };
  }

  /**
   * Binds the White Label checkbox event listener.
   */
  bindWhiteLabelCheckbox() {
    const whiteLabelCheckbox = /** @type {HTMLInputElement|null} */ (
      document.querySelector('#additional-wite-label')
    );
    if (!whiteLabelCheckbox) return;
    whiteLabelCheckbox.onchange = async (event) => {
      const target = /** @type {HTMLInputElement} */ (event.target);
      const isChecked = target.checked;
      target.disabled = true;
      try {
        if (isChecked) {
          await auCartApiClient.setWhiteLabelCartAttribute();
        } else {
          await auCartApiClient.deleteWhiteLabelCartAttribute();
        }
      } catch (error) {
        console.error('Error while updating white label option', error);
      } finally {
        target.disabled = false;
      }
    };
  }

  /**
   * Creates a debounced version of the provided function.
   *
   * The debounced function will delay its execution by `wait` milliseconds after it's last called.
   * If `immediate` is set to `true`, the function will be invoked immediately on the first call,
   * and then only after the wait period has passed since the last subsequent invocation.
   *
   * @param {(...args: any[]) => void} func - Function to debounce.cart__items
   * @param {number} wait - Delay in milliseconds.
   * @param {boolean} [immediate=false] - If true, triggers the function on the leading edge instead of the trailing.
   *
   * @returns {(...args: any[]) => void} A debounced version of the provided function.
   */
  auDebounce(func, wait, immediate) {
    let timeout;
    return function executedFunction() {
      const context = this;
      const args = arguments;
      function later() {
        timeout = null;
        if (!immediate) func.apply(context, args);
      }
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func.apply(context, args);
    };
  }

  /**
   * Removes all rush fee items from the cart.
   * @returns {Promise<void>}
   */
  async deleteAllRushFeeProducts() {
    try {
      const promises = [];
      this.cartItems.forEach((item) => {
        if (item?.handle === this.RUSH_FEE_HANDLER) {
          promises.push(auCartApiClient.removeLineItem(item.key));
        }
      });
      await Promise.all(promises).finally(async () => {
        const cartData = await auCartApiClient.getCart();
        auCartDomHelper.updateSubTotal(cartData);
      });
    } catch (error) {
      console.log('Error while deleting all rushFee products', error);
    }
  }

  /**
   * Removes extra side items that are no longer needed.
   * @returns {Promise<void>}
   */
  async removeObsoleteExtraSides() {
    try {
      const promises = [];
      const itemGroups = new Map();
      // Group items by _group for performance
      this.cartItems.forEach((item) => {
        const groupGuid = item.properties?._group;
        if (groupGuid) {
          if (!itemGroups.has(groupGuid)) {
            itemGroups.set(groupGuid, []);
          }
          itemGroups.get(groupGuid).push(item);
        }
      });
      this.cartItems.forEach((item) => {
        const itemGroupGuid = item.properties?._group;
        const cartItemsGroup = itemGroups.get(itemGroupGuid) || [];
        if (
          item.handle === this.EXTRA_SIDE_HANDLER &&
          cartItemsGroup.length === 1
        ) {
          promises.push(auCartApiClient.removeLineItem(item.key));
        }
      });
      await Promise.all(promises).finally(async () => {
        const cartData = await auCartApiClient.getCart();
        auCartDomHelper.updateSubTotal(cartData);
      });
    } catch (error) {
      console.error('Error removing obsolete extra sides:', error);
    }
  }

  /**
   * Checks if rush fee is available for all main items with `_projectId`.
   *
   * @param {Array<ShopifyCartItem>} items - Cart items from Shopify.
   * @returns {boolean} True if rush fee is available for all eligible items.
   */
  isAvailableRushFee(items) {
    if (!items.length) return false;
    const mainItems = items.filter(
      (item) => item.properties && item.properties._projectId
    );
    const itemsWithFastTrack = mainItems.filter(
      (item) => item.properties._hasFastTrack === true
    );
    return mainItems.length === itemsWithFastTrack.length;
  }

  /**
   * Checks if white label customization is available for the current cart.
   *
   * White label is not available if any item has `_isAlwaysWhiteLabel === true`.
   *
   * @param {Array<ShopifyCartItem>} items - Array of Shopify cart items.
   * @returns {boolean} True if white label is available.
   */
  isAvailableWhiteLabel(items) {
    if (items.length === 0) return false;
    return !items.some(
      (item) => item.properties?._isAlwaysWhiteLabel === true
    );
  }

  /**
   * Fetches cart data and updates internal state.
   *
   * @returns {Promise<void>}
   */
  async initializeCartAndUI() {
    try {
      const cartData = await auCartApiClient.getCart();
      auCartDomHelper.updateSubTotal(cartData);
      if (!cartData) throw new Error('Cart data is empty');
      this.cartItems = cartData.items;
      if (this.cartItems.length === 0) {
        window.location.reload();
      }
    } catch (error) {
      console.log('Error while fetching cart data', error);
    }
  }

  updateUIBasedOnCartState() {
    if (this.isAvailableRushFee(this.cartItems)) {
      auCartDomHelper.enableRushFee();
    } else {
      auCartDomHelper.disableRushFee();
    }
    if (this.isAvailableWhiteLabel(this.cartItems)) {
      auCartDomHelper.enableWhiteLabel();
    } else {
      auCartDomHelper.disableWhiteLabel();
    }
  }

  /**
   * Removes all rush fee description elements from DOM.
   */
  removeRushFeeNotificationElements() {
    document
      .querySelectorAll('div.rush-fee-extra-text')
      .forEach((element) => element.remove());
  }

  /**
   * Retrieves cached preview items or fetches them if not available.
   * @returns {PreviewItem[] | null}
   */
  getPreviewItemsFromCache() {
    const cachedData = localStorage.getItem('__auCartData');
    return cachedData ? JSON.parse(cachedData) : null;
  }

  /**
   * Gets preview URLs for a given cart item.
   * @param {ShopifyCartItem} cartItem
   * @returns {Promise<string[]>}
   */
  async getItemPreviews(cartItem) {
    const cachedPreviewItems = this.getPreviewItemsFromCache();
    const cachedPreview = cachedPreviewItems?.find(
      (item) => item.key === cartItem.key
    );
    if (cachedPreview) {
      return /** @type {string[]} */ (
        cachedPreview.previews.filter(Boolean)
      );
    }
    if (cartItem.properties && cartItem.properties._projectId) {
      const rawProjectData = await auCartApiClient.getRawProject(cartItem);
      if (!rawProjectData) return [];
      const fields = rawProjectData.line_items[0]?.properties?._fields;
      return [fields.PreviewFront, fields.PreviewBack].filter(Boolean);
    }
    return [];
  }

  /**
   * Updates the preview images for a cart item.
   * @param {string} itemKey
   * @param {string[]} previews
   * @returns {HTMLDivElement|null}
   */
  updateCartItemPreviewImages(itemKey, previews) {
    const imageWrapper = /** @type {HTMLDivElement|null} */ (
      document.querySelector(`div.cart-image[data-key='${itemKey}']`)
    );
    if (!imageWrapper) return null;
    imageWrapper.style.width = '150px';
    imageWrapper.style.marginRight = '40px';
    const imagesContainer = /** @type {HTMLDivElement|null} */ (
      imageWrapper.querySelector('div.images-parent')
    );
    if (!imagesContainer) return imageWrapper;
    imagesContainer.innerHTML = '';
    previews.forEach((previewUrl, index) => {
      if (!previewUrl) return;
      const img = document.createElement('img');
      img.src = previewUrl;
      img.style.width = '150px';
      img.style.height = '150px';
      if (index === previews.length - 1) {
        img.style.marginRight = '39px';
      }
      imagesContainer.appendChild(img);
    });
    return imageWrapper;
  }

  /**
   * Hides line item visually and updates quantity input value.
   * @param {ShopifyCartItem} cartItem
   */
  hideLineItem(cartItem) {
    const itemRow = auCartDomHelper.getItemRowElement(cartItem.key);
    if (!itemRow) return;
    itemRow.style.display = 'none';
    const quantityCell = document.querySelector(
      `td.cart-item__quantity[data-key='${cartItem.key}']`
    );
    if (!quantityCell) return;
    const quantityInput = quantityCell.querySelector('input');
    if (!quantityInput) return;
    quantityInput.value = String(cartItem.quantity);
  }

  /**
   * Subscribes to remove button click event for a cart item.
   * @param {ShopifyCartItem} cartItem
   */
  subscribeToRemoveLineItem(cartItem) {
    const button = /** @type {HTMLDivElement|null} */ (
      document.querySelector(`cc-cart-remove-button[data-key='${cartItem.key}']`)
    );
    if (!button) return;
    const linkElement = /** @type {HTMLAnchorElement|null} */ (
      button.querySelector('a')
    );
    if (!linkElement) return;
    linkElement.removeAttribute('href');
    linkElement.style.cursor = 'pointer';
    linkElement.onclick = async () => {
      auCartDomHelper.setDisabledCheckout(true);
      document.body.style.cursor = 'wait';
      const relatedProducts = this.findRelatedProducts(cartItem) || [cartItem];
      for (const product of relatedProducts) {
        await auCartApiClient.updateLineItemQuantity(product.key, 0);
      }
      if (this.isAddRushFee) {
        const rushFeeItem = this.cartItems.find(
          (item) => item.handle === this.RUSH_FEE_HANDLER
        );
        if (rushFeeItem) {
          await auCartApiClient.removeLineItem(rushFeeItem.key);
        }
      }
      document.body.style.cursor = 'default';
      document.dispatchEvent(new CustomEvent('au_update_cart'));
      for (const product of relatedProducts) {
        const rowElement = auCartDomHelper.getItemRowElement(product.key);
        if (rowElement) {
          rowElement.remove();
        }
      }
      window.location.reload();
    };
  }

  /**
   * Adds rush fee description to item row.
   * @param {ShopifyCartItem} item
   * @param {ShopifyCartItem} rushFeeItem
   */
  addRushFeeDescription(item, rushFeeItem) {
    const itemRowElement = auCartDomHelper.getItemRowElement(item.key);
    if (!itemRowElement) return;
    const descriptionList = itemRowElement.querySelector('dl');
    if (!descriptionList) return;
    descriptionList.innerHTML += `
      <div class="rush-fee-extra-text product-option">
        <dt>Rush Fee: </dt>
        <dd>+${auCartUtils.formatPrice(rushFeeItem.price)}</dd>
      </div>
    `;
  } 

  /**
   * Updates total price cell with extras included.
   * @param {ShopifyCartItem} cartItem
   */
  updatePriceWithExtras(cartItem) {
    const extraSideItem = this.findExtraSideItem(cartItem);
    const rushFeeItem = this.findRushFeeItem();

    const row = auCartDomHelper.getItemRowElement(cartItem.key);
    if (!row) return;
    const originalPriceElements = /** @type {Array<HTMLDivElement>} */ (Array.from(
      row.querySelectorAll('.cart-item__totals .cart-item__discounted-prices + .price.price--end')
    ));
    const finalPriceElements = /** @type {Array<HTMLDivElement>} */ (Array.from(
      row.querySelectorAll('.cart-item__totals .cart-item__discounted-prices')
    ));

    let fullOriginalPrice = cartItem.original_price;
    let fullFinalPrice = cartItem.final_price;

    if (extraSideItem) {
      fullOriginalPrice += extraSideItem.price;
      fullFinalPrice += extraSideItem.price;
    }
    if (rushFeeItem && this.isAddRushFee && this.isFastTrack(cartItem)) {
      fullOriginalPrice += rushFeeItem.price;
      fullFinalPrice += rushFeeItem.price;
    }

    fullOriginalPrice *= cartItem.quantity;
    fullFinalPrice *= cartItem.quantity;

    const hasDiscount = cartItem.original_price != cartItem.final_price;
    if (hasDiscount) {
      finalPriceElements.forEach((finalPriceElement) => {
        const originalTextElement = /** @type {HTMLDivElement|null} */ (
          finalPriceElement.querySelector('.price.price--end bdi')
        );
        const finalTextElement = /** @type {HTMLDivElement|null} */ (
          finalPriceElement.querySelector('dd.price.price--end bdi')
        );
        if (finalTextElement) {
          finalTextElement.textContent = auCartUtils.formatPrice(fullFinalPrice);
        }
        if (originalTextElement) {
          originalTextElement.textContent = auCartUtils.formatPrice(fullOriginalPrice);
        }
      });
    } else {
      originalPriceElements.forEach((originalPriceElement) => {
        const textElement = /** @type {HTMLDivElement|null} */ (
          originalPriceElement.querySelector('bdi')
        );
        if (textElement) {
          textElement.textContent = auCartUtils.formatPrice(fullOriginalPrice);
        }
      });
    }
  }

  /**
   * Finds rush fee item in cart.
   * @returns {ShopifyCartItem|undefined}
   */
  findRushFeeItem() {
    return this.cartItems.find(
      (item) => item.handle === this.RUSH_FEE_HANDLER
    );
  }

  /**
   * Finds extra side item related to the given item.
   * @param {ShopifyCartItem} item
   * @returns {ShopifyCartItem|undefined}
   */
  findExtraSideItem(item) {
    return this.cartItems.find(
      (cartItem) =>
        cartItem.properties &&
        cartItem.properties._group &&
        cartItem.properties._group === item.properties._group &&
        cartItem.handle === this.EXTRA_SIDE_HANDLER
    );
  }

  /**
   * Handles rendering logic for main product rows.
   * @param {boolean|null|undefined} isInitial
   * @param {ShopifyCartItem} item
   */
  handleMainProduct(isInitial, item) {
    const extraSideItem = this.findExtraSideItem(item);
    const rushFeeItem = this.findRushFeeItem();
    auCartDomHelper.addExtraSideDescription(isInitial, extraSideItem, item);
    this.updatePriceWithExtras(item);
    auCartDomHelper.changeDiscountUnitPrice(item);
    this.changeViewDiscountPriceIfExists(item);
    this.subscribeToRemoveLineItem(item);
    if (this.isFastTrack(item) && rushFeeItem && this.isAddRushFee) {
      this.addRushFeeDescription(item, rushFeeItem);
    }
  }

  /**
   * Renders warning for fast track unavailability.
   * @param {string} itemKey
   */
  renderFastTrackNotAvailableWarning(itemKey) {
    const itemRow = auCartDomHelper.getItemRowElement(itemKey);
    if (!itemRow) return;
    const detailsCell = itemRow.querySelector('td.cart-item__details');
    if (!detailsCell) return;
    const warningIcon = auCartDomHelper.getWarningIcon();
    if (!warningIcon) return;
    detailsCell.innerHTML += `
      <div style="display: flex; margin-top: 8px;">
        <div style="margin-right: 10px;">${warningIcon}</div>
        <div>Rush service not available on this item</div>
      </div>
    `;
  }

  /**
   * Finds related products based on group and project ID.
   * @param {ShopifyCartItem} cartItem
   * @returns {Array<ShopifyCartItem>}
   */
  findRelatedProducts(cartItem) {
    const groupGuid = cartItem.properties?._group;
    const projectId = cartItem.properties?._projectId;
    let relatedByGroup = [];
    if (groupGuid) {
      relatedByGroup = this.cartItems.filter(
        (item) =>
          item.properties &&
          item.properties._group === groupGuid
      );
    }
    const extraSideItems = this.cartItems.filter(
      (item) =>
        item.handle === this.EXTRA_SIDE_HANDLER &&
        item.properties?._group === groupGuid
    );
    const rushFeeItems = this.cartItems.filter(
      (item) =>
        item.handle === this.RUSH_FEE_HANDLER &&
        item.properties?._projectId === projectId
    );
    const allRelated = [
      cartItem,
      ...relatedByGroup,
      ...extraSideItems,
      ...rushFeeItems
    ];
    return [...new Map(allRelated.map((p) => [p.key, p])).values()];
  }

  /**
   * Handles rendering logic for discount price.
   * @param {ShopifyCartItem} cartItem 
   */
  changeViewDiscountPriceIfExists(cartItem) {
    const row = auCartDomHelper.getItemRowElement(cartItem.key);
    if (!row) return;
    const discountPriceElements = /** @type {Array<HTMLDivElement>} */ (Array.from(
      row.querySelectorAll('.cart-item__discounted-prices')
    ));
    const originalPriceElements = /** @type {Array<HTMLDivElement>} */ (Array.from(
      row.querySelectorAll('.cart-item__discounted-prices + .price.price--end')
    ));

    const hasDiscount = cartItem.original_price != cartItem.final_price;
    if (hasDiscount) {
      discountPriceElements.forEach((discountPriceElement) => {
        discountPriceElement.style.display = 'flex';
      });
      originalPriceElements.forEach((originalPriceElement) => {
        originalPriceElement.style.display = 'none';
      });
    } else {
      discountPriceElements.forEach((discountPriceElement) => {
        discountPriceElement.style.display = 'none';
      });
      originalPriceElements.forEach((originalPriceElement) => {
        originalPriceElement.style.display = 'flex';
      });
    }
  }

  /**
   * global update discount price view
   */
  async globalUpdateDiscountPriceView() {
    this.cartItems.forEach((item) => {
      auCartDomHelper.changeDiscountUnitPrice(item);
      this.changeViewDiscountPriceIfExists(item);
    });
  }

  /**
   * Updates quantities of related products.
   * @param {Array<ShopifyCartItem>} products
   * @param {number} quantity
   * @returns {Promise<void>}
   */
  async updateLineItemsQuantities(products, quantity) {
    for (const product of products) {
      const relatedProducts = this.findRelatedProducts(product);
      const allToUpdate = [product, ...relatedProducts];
      const uniqueToUpdate = Array.from(
        new Map(allToUpdate.map((p) => [p.key, p])).values()
      );
      for (const item of uniqueToUpdate) {
        try {
          const changedCart = await auCartApiClient.changeLineItemQuantity(
            item.key,
            quantity
          );
          if (!changedCart?.items) continue;
          this.cartItems = changedCart.items;
          const updatedItem = this.cartItems.find(
            (cartItem) =>
              cartItem.properties?._group === item.properties?._group &&
              cartItem.variant_id === item.variant_id
          );
          if (updatedItem && updatedItem.key !== item.key) {
            auCartDomHelper.replaceLineItemKey(item.key, updatedItem.key);
          }
          this.updateQuantityValue(updatedItem?.key || item.key, quantity);
        } catch (error) {
          console.error('Error updating line item quantity:', error);
        }
      }
    }
  }

  /**
   * Adjusts the quantity of the rush fee item based on total quantity of all main items.
   * @param {ShopifyCartItem} cartItem
   * @param {number} newQuantity
   * @returns {Promise<void>}
   */
  async adjustRushFeeQuantity(cartItem, newQuantity) {
    const mainItems = this.cartItems.filter(
      (item) =>
        item.properties &&
        item.properties._projectId &&
        item.handle !== this.EXTRA_SIDE_HANDLER &&
        item.handle !== this.RUSH_FEE_HANDLER
    );
    const currentMainIndex = mainItems.findIndex(
      (item) => item.key === cartItem.key
    );
    if (currentMainIndex !== -1) {
      mainItems[currentMainIndex].quantity = newQuantity;
    }
    const totalMainQuantity = mainItems.reduce(
      (acc, item) => acc + item.quantity,
      0
    );
    const rushFeeItem = this.findRushFeeItem();
    if (!rushFeeItem) return;
    if (totalMainQuantity > 0) {
      await auCartApiClient.updateLineItemQuantity(
        rushFeeItem.key,
        totalMainQuantity
      );
    } else {
      await auCartApiClient.removeLineItem(rushFeeItem.key);
    }
  }

  /**
   * Handles quantity change for a cart item and related products.
   * @param {ShopifyCartItem} cartItem
   * @param {string|number|null|undefined} newValue
   * @returns {Promise<void>}
   */
  async handleQuantityChange(cartItem, newValue) {
    auCartDomHelper.setDisabledCheckout(true);

    const submitButton = /** @type {HTMLButtonElement|null} */ (
      document.querySelector("button[type='submit'][name='checkout']")
    );
    
    const quantityButtons = /** @type {Array<HTMLButtonElement>} */ (Array.from(
      document.querySelectorAll('.quantity__button')
    ));
    const quantityInputs = /** @type {Array<HTMLInputElement>} */ (Array.from(
      document.querySelectorAll('.quantity__input')
    ));

    const parsedValue = auCartUtils.parseQuantityInput(newValue);
    if (!Number.isFinite(parsedValue)) return;

    try {
      auCartDomHelper.disableCartUI(quantityButtons, quantityInputs);
      const adjustedValue = Math.max(0, parsedValue);
      const relatedProducts = this.findRelatedProducts(cartItem) || [cartItem];
      await this.updateLineItemsQuantities(relatedProducts, adjustedValue);
      if (this.isAddRushFee) {
        await this.adjustRushFeeQuantity(cartItem, adjustedValue);
      }
      auCartDomHelper.enableCartUI(quantityButtons, quantityInputs);
      document.dispatchEvent(new CustomEvent('au_update_cart'));
      if (adjustedValue === 0) {
        auCartDomHelper.removeCartItemsFromDOM(relatedProducts);
      }
      auCartDomHelper.setDisabledCheckout(true);
    } catch (error) {
      console.error('Error while updating cart quantity', error);
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
      }
      await this.globalUpdateDiscountPriceView();
      window.location.reload();
    }
  }

  /**
   * Binds input onchange handler.
   * @param {ShopifyCartItem} cartItem
   * @param {HTMLInputElement} input
   */
  bindQuantityInputChange(cartItem, input) {
    input.onchange = (event) => {
      const target = /** @type {HTMLInputElement} */ (event.target);
      this.handleQuantityChange(cartItem, target.value);
    };
  }

  /**
   * Binds input onkeyup handler.
   * @param {HTMLInputElement} input
   */
  bindQuantityInputKeyup(input) {
    input.onkeyup = this.auDebounce(
      (event) => {
        const target = /** @type {HTMLInputElement} */ (event.target);
        if (
          event.code &&
          ['enter', 'numpadenter'].includes(event.code.toLowerCase())
        ) {
          event.preventDefault();
          target.dispatchEvent(new Event('change'));
          return false;
        }
        if (target.value !== '') {
          target.dispatchEvent(new Event('change'));
        }
      },
      1000,
      false
    );
  }

  /**
   * Updates quantity input value.
   * @param {string} itemKey
   * @param {number} [quantity]
   */
  updateQuantityValue(itemKey, quantity) {
    const input = /** @type {HTMLInputElement|null} */ (
      document.querySelector(`input[data-key="${itemKey}"]`)
    );
    if (!input) return;
    const cartItem = this.cartItems.find((i) => i.key === itemKey);
    input.value = String(quantity ?? cartItem?.quantity ?? 0);
  }

  /**
   * Sets up quantity input with buttons and event listeners.
   * @param {string} itemKey
   */
  setupQuantityInput(itemKey) {
    const quantityCell = document.querySelector(
      `td.cart-item__quantity[data-key="${itemKey}"]`
    );
    if (!quantityCell) return;
    const input = /** @type {HTMLInputElement|null} */ (
      document.querySelector(`input[data-key="${itemKey}"]`)
    );
    if (!input) return;
    const cartItem = this.cartItems.find((i) => i.key === itemKey);
    if (!cartItem) {
      console.warn('Cart item not found for quantity input:', itemKey);
      return;
    }
    this.updateQuantityValue(itemKey);
    const minusButton = quantityCell.querySelector('button[name="minus"]');
    const plusButton = quantityCell.querySelector('button[name="plus"]');
    this.bindQuantityInputChange(cartItem, input);
    this.bindQuantityInputKeyup(input);
    if (minusButton) {
      minusButton.addEventListener('click', () => {
        this.handleQuantityChange(
          cartItem,
          Math.max(0, Number(input.value) - 1)
        );
      });
    }
    if (plusButton) {
      plusButton.addEventListener('click', () => {
        this.handleQuantityChange(cartItem, Number(input.value) + 1);
      });
    }
  }

  /**
   * Cart item has option adding rush fee.
   * @param {ShopifyCartItem} cartItem 
   * @returns {boolean} is fast track
   */
  isFastTrack(cartItem) {
    return cartItem.properties?._hasFastTrack === true;
  }

  /**
   * Initializes UI for each cart item.
   * @param {boolean} [isInitial]
   */
  async updateCartUI(isInitial) {
    if (isInitial !== true) {
      await this.initializeCartAndUI();
    }
    if (!isInitial) {
      this.updateUIBasedOnCartState();
    }
    this.removeRushFeeNotificationElements();
    for (const item of this.cartItems) {
      if (isInitial) {
        const previews = await this.getItemPreviews(item);
        const imageWrapper = this.updateCartItemPreviewImages(item.key, previews);
        if (imageWrapper) {
          auCartUtils.initSlider(imageWrapper, previews);
        }
      }
      if (item.properties && item.properties._projectId) {
        this.handleMainProduct(isInitial, item);
        if (isInitial && !this.isFastTrack(item) && item.properties._projectId) {
          this.renderFastTrackNotAvailableWarning(item.key);
        }
        if (isInitial) {
          this.setupQuantityInput(item.key);
        }
      }
      auCartDomHelper.changeDiscountUnitPrice(item);
      this.changeViewDiscountPriceIfExists(item);
    }
    await this.initializeCartAndUI();
    auCartDomHelper.setDisabledCheckout(false);
  }
}